import { translateServerText } from './server-text';
import type {
  ArtworkBulkResult,
  ArtworkGroup,
  ArtworkIngestResult,
  ArtworkStatus,
  ArtworkUploadFields,
  ArtworkUploadResult,
  AssetKind,
  AssetWithUrls,
  FacetValue,
  Folder,
  Id,
  Paginated,
  SupportKind,
  SupportTicket,
} from '@shared/types';

export type {
  ArtworkBulkResult,
  ArtworkGroup,
  ArtworkIngestResult,
  ArtworkStatus,
  ArtworkUploadFields,
  ArtworkUploadResult,
  SupportKind,
  SupportTicket,
};

/**
 * One type-ahead suggestion. `kind` says which field it came from, which is
 * also what decides where selecting it navigates — a brand filters by brand,
 * a filename is free text.
 */
export interface Suggestion {
  value: string;
  kind: 'brand' | 'tag' | 'imageType' | 'filename' | 'product' | 'folder';
  /** Roughly how many assets sit behind it; drives ordering server-side. */
  count: number;
}

/** Artwork list filters. Brand is exact; sap/job are substring matches. */
export interface ArtworkFilter {
  q?: string;
  brand?: string;
  sap?: string;
  job?: string;
  limit?: number;
}

/** Console entries captured by the support pill and shipped with a ticket. */
export interface BrowserLog {
  timestamp: string;
  level: string;
  message: string;
}

export interface TicketInput {
  kind: SupportKind;
  message: string;
  pageUrl?: string;
  /** Required only when raising a login issue with no session. */
  contactEmail?: string;
  browserLogs?: BrowserLog[];
}

export class ApiError extends Error {
  /** The server's original (English) text, for code that branches on it. */
  readonly raw: string;
  constructor(readonly status: number, message: string) {
    super(translateServerText(message));
    this.raw = message;
    this.name = 'ApiError';
  }
  get isAuthError() {
    return this.status === 401;
  }
}

const CSRF = { 'X-Requested-With': 'ecomstat-dam' } as const;

async function request<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const { method = 'GET', body } = opts;
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(method === 'GET' ? {} : CSRF),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const e = (await res.json()) as { error?: string };
      if (e.error) msg = e.error;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, msg);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export interface TaggedAssetWithUrls extends AssetWithUrls {
  aiTags: {
    brand: string;
    productTitle: string;
    imageType: string;
    description: string;
    hashtags: string;
    confirmed: boolean;
  } | null;
}

export interface AssetFilter {
  folderId?: Id;
  recursive?: boolean;
  /**
   * 'pdf' is a filter-only pseudo-kind: Pimcore has no PDF type (they land under
   * `document` or even `image`), so the server matches it on mime instead.
   */
  kind?: AssetKind | 'pdf' | 'font';
  search?: string;
  tag?: string;
  /** Top-level country/market folder, e.g. 'ES'. */
  location?: string;
  untagged?: boolean;
  favourites?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    // Arrays repeat the key (?fileTypes=png&fileTypes=pdf). String()-ing one
    // would send a single "png,pdf" value that matches nothing.
    if (Array.isArray(v)) {
      for (const item of v) if (item !== undefined && item !== '') p.append(k, String(item));
    } else {
      p.set(k, String(v));
    }
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  me: () => request<{ user: { id: number; username: string; name: string; email: string | null; isAdmin: boolean } }>('/me'),
  /**
   * Either signs in outright (`user`) or asks for the emailed code
   * (`challenge`) — the password was still correct in both cases.
   */
  login: (username: string, password: string) =>
    request<{ user?: unknown; challenge?: { handle: string; hint: string | null } }>('/auth/login', {
      method: 'POST',
      body: { username, password },
    }),
  submitLoginCode: (handle: string, code: string, remember: boolean) =>
    request<{ user: unknown }>('/auth/challenge', {
      method: 'POST',
      body: { handle, code, remember },
    }),
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/auth/password/forgot', { method: 'POST', body: { email } }),
  resetPassword: (token: string, password: string) =>
    request<{ ok: true }>('/auth/password/reset', { method: 'POST', body: { token, password } }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; sessions: number; devices: number }>('/account/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  signOutEverywhere: () =>
    request<{ ok: true; sessions: number; devices: number }>('/account/sign-out-everywhere', { method: 'POST' }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  // Magic-link (passwordless) login
  requestMagicLink: (email: string) => request<{ ok: true; devLink?: string }>('/auth/magic/request', { method: 'POST', body: { email } }),
  register: (email: string, name: string) => request<{ ok: true }>('/auth/register', { method: 'POST', body: { email, name } }),
  consumeMagicToken: (token: string) => request<{ user: unknown }>('/auth/magic/consume', { method: 'POST', body: { token } }),
  createShareLink: (userId?: number) => request<{ link: string }>('/auth/share', { method: 'POST', body: userId ? { userId } : {} }),
  sendTestEmail: () => request<{ ok: true }>('/auth/test-email', { method: 'POST' }),

  folders: () => request<{ items: Folder[]; tree: Folder[] }>('/folders'),
  createFolder: (name: string, parentId: Id | null) => request<{ folder: Folder }>('/folders', { method: 'POST', body: { name, parentId } }),
  renameFolder: (id: Id, name: string) => request<{ ok: true }>(`/folders/${id}`, { method: 'PATCH', body: { name } }),
  deleteFolder: (id: Id) =>
    request<{ ok: true; recycleBinId: number | null; name: string }>(`/folders/${id}`, { method: 'DELETE' }),

  assets: (f: AssetFilter) => request<Paginated<AssetWithUrls>>(`/assets${qs(f as Record<string, unknown>)}`),
  asset: (id: Id) => request<{ asset: AssetWithUrls }>(`/assets/${id}`),
  /**
   * Which sized-download options this asset offers. `derivable` is false for
   * anything we can't rasterise (PDF, video), so the UI can offer the original
   * alone instead of buttons that would 415.
   */
  downloadFormats: (id: Id) =>
    request<{
      derivable: boolean;
      /**
       * Whether the Cutting (die-cut mockup) download is on offer. Narrower than
       * `derivable`: it needs a JPEG/PNG source *and* ImageMagick on the server.
       */
      cutting: boolean;
      /**
       * Whether this particular image carries a Photoshop clipping path. Only
       * ~8% of the library does, so the Cutting row uses this to describe what
       * it will actually produce instead of always claiming to keep a path.
       */
      clippingPath: boolean;
      formats: { id: string; label: string; description: string }[];
      customTypes: { id: string; label: string }[];
    }>(`/assets/${id}/download-formats`),
  /** Reports which backend is live and whether writes are permitted. */
  health: () => request<{ ok: boolean; backend: string; readOnly?: boolean }>('/health'),
  updateMetadata: (id: Id, metadata: Record<string, unknown>) => request<{ asset: AssetWithUrls }>(`/assets/${id}/metadata`, { method: 'PATCH', body: metadata }),
  moveAsset: (id: Id, folderId: Id | null) => request<{ ok: true }>(`/assets/${id}/move`, { method: 'POST', body: { folderId } }),
  // `recycleBinId` is the undo handle — null when the delete cannot be undone.
  deleteAsset: (id: Id) =>
    request<{ ok: true; recycleBinId: number | null; fileName: string | null }>(`/assets/${id}`, { method: 'DELETE' }),
  restoreAsset: (recycleBinId: number) =>
    request<{ ok: true; id: number | null; path: string }>(`/assets/restore/${recycleBinId}`, { method: 'POST' }),
  /** What is still in the recycle bin: own deletions, or everyone's for admins. */
  recycleBin: () => request<{ items: RecycleBinItem[] }>('/recyclebin'),

  upload: async (folderId: Id | null, file: File) => {
    const form = new FormData();
    form.append('file', file);
    if (folderId) form.append('folderId', String(folderId));
    const res = await fetch('/api/upload', { method: 'POST', credentials: 'same-origin', headers: { ...CSRF }, body: form });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, e.error ?? res.statusText);
    }
    return (await res.json()) as { asset: AssetWithUrls };
  },

  // AI tagging
  tagNow: (id: Id) => request<{ tags: unknown }>(`/assets/${id}/tag`, { method: 'POST' }),
  confirmTags: (id: Id, confirmed: boolean) => request<{ ok: true }>(`/assets/${id}/tag/confirm`, { method: 'POST', body: { confirmed } }),
  getPrompt: () => request<{ prompt: string }>('/tags/prompt'),
  setPrompt: (prompt: string) => request<{ ok: true }>('/tags/prompt', { method: 'PUT', body: { prompt } }),
  enqueueUntagged: () => request<{ enqueued: number }>('/tags/enqueue-untagged', { method: 'POST' }),
  drain: (limit = 10) => request<{ done: number; failed: number }>(`/tags/drain?limit=${limit}`, { method: 'POST' }),
  queueStats: () => request<{ stats: Record<string, number> }>('/tags/queue'),

  // meta-tag search
  search: (params: Record<string, unknown>) => request<Paginated<TaggedAssetWithUrls>>(`/search${qs(params)}`),
  /**
   * Filter options. Passing the active query makes the image/file type counts
   * reflect it — each is counted with its own selection excluded, so a pill
   * says what it would return rather than what is already selected.
   */
  facets: (params: Record<string, unknown> = {}) =>
    request<{
      brands: string[];
      imageTypes: FacetValue[];
      /** File extensions on Pimcore; asset kinds on the native backend. */
      fileTypes: FacetValue[];
      /** Country/market folders; empty on the native backend. */
      locations: { name: string; path: string; count: number }[];
    }>(`/search/facets${qs(params)}`),

  // library stats + favourites
  libraryStats: (folderId?: Id) => request<LibraryStats>(`/library/stats${folderId ? `?folderId=${folderId}` : ''}`),
  favouriteIds: () => request<{ ids: number[] }>('/favourites/ids'),
  toggleFavourite: (id: Id) => request<{ favourited: boolean }>(`/assets/${id}/favourite`, { method: 'POST' }),

  // users (admin)
  users: () => request<{ items: DamUser[] }>('/users'),
  setUserActive: (id: Id, isActive: boolean) => request<{ ok: true }>(`/users/${id}/active`, { method: 'POST', body: { isActive } }),
  setUserAdmin: (id: Id, isAdmin: boolean) => request<{ ok: true }>(`/users/${id}/admin`, { method: 'POST', body: { isAdmin } }),

  // activity log (admin)
  activity: (f: ActivityFilter) => request<{ items: ActivityRow[]; total: number }>(`/activity${qs(f as Record<string, unknown>)}`),
  activityStats: (days: number) => request<UsageStats>(`/activity/stats?days=${days}`),
  pruneActivity: (days: number) => request<{ deleted: number }>('/activity/prune', { method: 'POST', body: { days } }),

  // share basket
  basket: () => request<{ items: BasketItem[] }>('/basket'),
  addToBasket: (body: { assetId: Id; size?: number; quality?: number | null; dpi?: number | null }) =>
    request<{ items: BasketItem[] }>('/basket', { method: 'POST', body }),
  removeFromBasket: (assetId: Id) => request<{ items: BasketItem[] }>(`/basket/${assetId}`, { method: 'DELETE' }),
  clearBasket: () => request<{ ok: true }>('/basket', { method: 'DELETE' }),

  // share links
  /* ---------- type-ahead ---------- */
  suggest: (q: string) => request<{ items: Suggestion[] }>(`/search/suggest${qs({ q })}`),

  /* ---------- artwork index ---------- */
  artworkStatus: () => request<ArtworkStatus>('/artwork/status'),
  artworkBrands: () => request<{ items: string[] }>('/artwork/brands'),
  artwork: (p: ArtworkFilter) => request<{ items: ArtworkGroup[] }>(`/artwork${qs({ ...p })}`),
  artworkGroup: (pdfKey: string) =>
    request<ArtworkGroup>(`/artwork/${encodeURIComponent(pdfKey)}`),
  // A 409 comes back as a result (error 'exists'), not a throw, so the form can
  // ask before replacing.
  artworkUpload: async (fields: ArtworkUploadFields, pdf: File, replace: boolean) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) form.append(k, v);
    form.append('pdf', pdf);
    if (replace) form.append('replace', 'true');
    const res = await fetch('/api/artwork/upload', { method: 'POST', credentials: 'same-origin', headers: { ...CSRF }, body: form });
    const body = (await res.json().catch(() => ({}))) as ArtworkUploadResult;
    if (!res.ok && res.status !== 409) throw new ApiError(res.status, body.error ?? res.statusText);
    return body;
  },
  artworkIngest: () => request<ArtworkIngestResult>('/artwork/ingest', { method: 'POST' }),
  artworkBulk: async (files: File[], brand: string) => {
    const form = new FormData();
    for (const f of files) form.append('pdf', f);
    if (brand) form.append('brand', brand);
    const res = await fetch('/api/artwork/bulk', { method: 'POST', credentials: 'same-origin', headers: { ...CSRF }, body: form });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, e.error ?? res.statusText);
    }
    return (await res.json()) as ArtworkBulkResult;
  },

  shares: () => request<{ shares: ShareLink[] }>('/shares'),
  createShare: (body: { expiryDays: number; title?: string | null }) =>
    request<{ share: ShareLink; url: string }>('/shares', { method: 'POST', body }),
  revokeShare: (id: Id) => request<{ ok: true }>(`/shares/${id}/revoke`, { method: 'POST' }),

  /** Recipient side — public, no session required. */
  sharedView: (token: string) =>
    request<{ title: string | null; expiresAt: string; assets: SharedAsset[] }>(`/share/${token}`),

  // support tickets — POST works signed-out too (login issues); GET is admin-only
  raiseTicket: (body: TicketInput) =>
    request<{ id: number; supportTicketNumber: string | null }>('/support', { method: 'POST', body }),
  supportTickets: () => request<{ tickets: SupportTicket[] }>('/support'),

  // saved download presets
  downloadPresets: () => request<{ presets: DownloadPreset[] }>('/download-presets'),
  createPreset: (body: PresetInput) => request<{ preset: DownloadPreset }>('/download-presets', { method: 'POST', body }),
  updatePreset: (id: Id, body: PresetInput) => request<{ preset: DownloadPreset }>(`/download-presets/${id}`, { method: 'PUT', body }),
  deletePreset: (id: Id) => request<{ ok: true }>(`/download-presets/${id}`, { method: 'DELETE' }),
};

export interface RecycleBinItem {
  id: number;
  name: string;
  /** The folder the item was deleted from. */
  folder: string;
  isFolder: boolean;
  childCount: number;
  mimetype: string | null;
  deletedAt: string;
  deletedBy: string | null;
}

export interface BasketItem {
  assetId: Id;
  /** Percentage of the original; 100 ships the original bytes untouched. */
  size: number;
  quality: number | null;
  dpi: number | null;
  fileName: string;
  path: string;
  kind: AssetKind;
  mime: string | null;
  width: number | null;
  height: number | null;
  thumbUrl: string | null;
}

export interface ShareLink {
  id: Id;
  /** Only populated at creation — tokens are hashed at rest, never re-readable. */
  url: string;
  title: string | null;
  itemCount: number;
  expiresAt: string;
  revokedAt: string | null;
  viewCount: number;
  downloadCount: number;
  createdAt: string;
  expired: boolean;
  active: boolean;
}

export interface SharedAsset {
  assetId: Id;
  fileName: string;
  kind: AssetKind;
  mime: string | null;
  size: number;
  width: number | null;
  height: number | null;
}

/** Recipient-side URLs; public, so they work without a session. */
export const shareUrls = {
  zip: (token: string) => `/api/share/${token}/download`,
  asset: (token: string, assetId: Id) => `/api/share/${token}/asset/${assetId}`,
};

export type CustomType = 'jpeg' | 'png' | 'webp' | 'tiff';
export type CustomMode = 'width' | 'height' | 'fit' | 'original';

export interface DownloadPreset {
  id: number;
  name: string;
  type: CustomType;
  mode: CustomMode;
  width: number | null;
  height: number | null;
  quality: number | null;
  dpi: number | null;
  shared: boolean;
  editable: boolean;
}

export interface PresetInput {
  name: string;
  type: CustomType;
  mode: CustomMode;
  width?: number | null;
  height?: number | null;
  quality?: number | null;
  dpi?: number | null;
  shared?: boolean;
}

/**
 * Hrefs for the download controls.
 *
 * These are plain URLs rather than fetch calls because downloading is what an
 * `<a download>` does natively — the browser streams to disk with a progress
 * indicator, and the session cookie authenticates it same-origin. Pulling a
 * 40 MB print derivative into memory via fetch just to re-emit it as a blob
 * would be slower and would lose that.
 */
export const downloadUrls = {
  // `?download=1` marks a deliberate download. The same endpoint backs inline
  // previews, and without the flag the server cannot tell the two apart — it
  // would raise a download alert every time a detail page rendered an image.
  original: (id: Id) => `/api/assets/${id}/raw?download=1`,
  format: (id: Id, format: string) => `/api/assets/${id}/download/${format}`,
  custom: (id: Id, spec: Record<string, unknown>) => `/api/assets/${id}/download-custom${qs(spec)}`,
  /**
   * The die-cut mockup export. `orientation` picks which axis is capped at
   * 1000px; 'auto' uses the image's own longer side and is what the button
   * sends unless someone opens the orientation choices.
   */
  cutting: (id: Id, orientation: CuttingOrientation = 'auto') =>
    `/api/assets/${id}/download-cutting?orientation=${orientation}`,
};

export type CuttingOrientation = 'auto' | 'landscape' | 'portrait';

export interface LibraryStats {
  total: number;
  byKind: Record<string, number>;
  untagged: number;
  favourites: number;
  storageBytes: number;
}

export interface DamUser {
  id: number;
  username: string;
  name: string;
  email: string | null;
  isAdmin: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/* ---------- activity log ---------- */

export interface ActivityRow {
  id: number;
  /** Event kind, e.g. 'asset.download' — mirrors AlertKind on the server. */
  kind: string;
  actor: string | null;
  userId: number | null;
  subject: string | null;
  assetId: number | null;
  ip: string | null;
  userAgent: string | null;
  detail: Record<string, string | number>;
  createdAt: string;
}

export interface ActivityFilter {
  kind?: string;
  actor?: string;
  userId?: number;
  assetId?: number;
  /** Only events from the last N days. */
  days?: number;
  limit?: number;
  offset?: number;
}

export interface UsageStats {
  since: string;
  byKind: { kind: string; n: number }[];
  topActors: { actor: string | null; n: number }[];
  topAssets: { assetId: number | null; subject: string | null; n: number }[];
  failedLogins: { ip: string | null; n: number }[];
}
